import React from "react";
import { ArrowLeft, PlayCircle, Loader2 } from "lucide-react";
import { StatusBadge } from "@/components/common/Badges";
import { TID } from "@/lib/testIds";

/** Semantic score colour — a healthy number must never read like an alarm.
 * `inverted` flips the scale for Risk, where high is bad. */
const scoreTone = (v, inverted = false) => {
    if (v === null || v === undefined) return "text-slate-400";
    const good = inverted ? v <= 30 : v >= 75;
    const mid = inverted ? v <= 60 : v >= 45;
    return good ? "text-emerald-400" : mid ? "text-amber-400" : "text-rose-400";
};


export function WorkspaceHeaderPanel({ ws, nav, runAnalysis, analyzing, artifactsLength }) {
    return (
        <div className="panel-elevated p-6 relative overflow-hidden">
            <div className="absolute -right-12 -top-12 w-64 h-64 rounded-full bg-cyan-500/5 blur-3xl pointer-events-none" />
            <button onClick={() => nav("/workspaces")} className="btn-ghost flex items-center gap-1 mb-4 w-fit">
                <ArrowLeft size={12} /> All Workspaces
            </button>
            <div className="flex flex-col md:flex-row gap-6 items-start md:items-center">
                <div className="flex-1 min-w-0">
                    <div className="label-eyebrow">Workspace · {ws.id.slice(0, 8)}</div>
                    <h1 className="font-display text-3xl text-white tracking-tight mt-1">{ws.name}</h1>
                    <p className="text-sm text-slate-400 mt-2 max-w-2xl">{ws.description || "—"}</p>
                    <div className="flex items-center gap-2 mt-3">
                        <StatusBadge status={ws.status} />
                        {ws.tags?.map((t) => (
                            <span key={t} className="px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-cyan-300/80 border border-cyan-500/20 rounded-sm">
                                {t}
                            </span>
                        ))}
                    </div>
                </div>
                <div className="grid grid-cols-3 gap-3 min-w-[340px]">
                    {[
                        { v: ws.risk_score?.toFixed?.(1) ?? "—", l: "Risk", c: scoreTone(ws.risk_score, true) },
                        { v: ws.confidence_score?.toFixed?.(1) ?? "—", l: "Confidence", c: scoreTone(ws.confidence_score) },
                        { v: ws.clean_core_score?.toFixed?.(1) ?? "—", l: "Clean Core", c: scoreTone(ws.clean_core_score) },
                    ].map((m) => (
                        <div key={m.l} className="panel px-3 py-2 text-center">
                            <div className={`font-display text-2xl ${m.c}`}>{m.v}</div>
                            <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{m.l}</div>
                        </div>
                    ))}
                </div>
                <button
                    data-testid={TID.workspaceAnalyzeBtn}
                    onClick={runAnalysis}
                    disabled={analyzing || artifactsLength === 0}
                    className="btn-primary flex items-center gap-2 disabled:opacity-40"
                >
                    {analyzing ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />}
                    {analyzing ? "Analyzing…" : "Run Analysis"}
                </button>
            </div>
        </div>
    );
}
